/**
 * AnalyticsManager — Admin Dashboard "Analytics" tab.
 *
 * Pulls /api/admin/analytics/overview and renders:
 *   • Library: total videos, hours, GB, live-vs-replay split, uploads sparkline
 *   • Engagement: logged-in views, peak concurrent, top games, clips
 *   • Audience: users, subscribers, leads, signup sparkline
 *   • Content: articles published
 *   • Honest "missing data" callout pointing at the CF token scope + PostHog key
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Video, Users, PlayCircle, TrendingUp, Eye, Crown, Newspaper, Scissors,
  Radio, AlertCircle, Loader2, BarChart3, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatCard({ icon: Icon, label, value, sub, accent = 'cyan' }) {
  const accents = {
    cyan: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400',
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    amber: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
    rose: 'bg-rose-500/10 border-rose-500/30 text-rose-400',
    violet: 'bg-violet-500/10 border-violet-500/30 text-violet-400',
  };
  return (
    <Card className="bg-zinc-900/60 border-zinc-800" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <span className="text-zinc-400 text-xs uppercase tracking-wide">{label}</span>
          <div className={`w-8 h-8 rounded-lg border flex items-center justify-center ${accents[accent]}`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
        <div className="text-3xl font-bold text-white">{value}</div>
        {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function Sparkline({ data, accent = '#22d3ee', height = 40 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(1, ...data.map(d => d.count));
  const w = 240;
  const h = height;
  const points = data.map((d, i) => {
    const x = (i / Math.max(1, data.length - 1)) * w;
    const y = h - (d.count / max) * h;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} className="block mt-2">
      <polyline points={points} fill="none" stroke={accent} strokeWidth="2" />
      <polyline points={`0,${h} ${points} ${w},${h}`} fill={accent} fillOpacity="0.08" />
    </svg>
  );
}

export default function AnalyticsManager({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/admin/analytics/overview?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (!res.ok) {
          toast.error('Failed to load analytics');
          return;
        }
        setData(await res.json());
      } catch {
        if (!cancelled) toast.error('Network error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days, token]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading analytics…
      </div>
    );
  }

  const { library, engagement, audience, content, missing_data_sources } = data;

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Header + window switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-cyan-400" />
            Analytics Overview
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Last {days} days · As of {new Date(data.as_of).toLocaleString()}
          </p>
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map(d => (
            <Button
              key={d}
              variant="ghost"
              size="sm"
              onClick={() => setDays(d)}
              className={`text-xs ${days === d ? 'bg-cyan-500/10 text-cyan-400' : 'text-zinc-400'}`}
              data-testid={`window-${d}d`}
            >
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {/* Missing data callout (honest) */}
      {missing_data_sources && missing_data_sources.length > 0 && (
        <Card className="bg-amber-500/5 border-amber-500/30" data-testid="missing-sources-callout">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-amber-300 font-semibold text-sm mb-1.5">Unlock more data</p>
                <ul className="text-xs text-zinc-400 space-y-1">
                  {missing_data_sources.map((m, i) => (
                    <li key={i} className="leading-relaxed">• {m}</li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AUDIENCE STATS */}
      <div>
        <h3 className="text-cyan-400 text-xs uppercase tracking-wider mb-3 font-semibold">Audience</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            icon={Users} label="Total Users" accent="cyan"
            value={audience.total_users.toLocaleString()}
            sub={`+${audience.new_users_in_window} in ${days}d`}
          />
          <StatCard
            icon={Crown} label="Active Subscribers" accent="emerald"
            value={audience.active_subscribers.toLocaleString()}
            sub="Paying members"
          />
          <StatCard
            icon={MapPin} label="Leads Captured" accent="amber"
            value={audience.leads_total.toLocaleString()}
            sub={`+${audience.leads_in_window} in ${days}d`}
          />
          <StatCard
            icon={Newspaper} label="Articles Live" accent="violet"
            value={content.articles_published.toLocaleString()}
            sub="Published SEO content"
          />
        </div>
        {audience.users_timeseries?.length > 0 && (
          <Card className="bg-zinc-900/60 border-zinc-800 mt-3">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-zinc-400 uppercase tracking-wide">New users · last {days} days</span>
                <span className="text-xs text-cyan-400">{audience.new_users_in_window} total</span>
              </div>
              <Sparkline data={audience.users_timeseries} accent="#22d3ee" />
            </CardContent>
          </Card>
        )}
      </div>

      {/* LIBRARY STATS */}
      <div>
        <h3 className="text-cyan-400 text-xs uppercase tracking-wider mb-3 font-semibold">Video Library (Cloudflare Stream)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            icon={Video} label="Total Videos" accent="violet"
            value={library.total_videos.toLocaleString()}
            sub={`${library.total_gigabytes} GB stored`}
          />
          <StatCard
            icon={PlayCircle} label="Hours of Content" accent="cyan"
            value={library.total_hours.toLocaleString()}
            sub="Available for replay"
          />
          <StatCard
            icon={Radio} label="Live Recordings" accent="rose"
            value={library.live_recordings.toLocaleString()}
            sub="Captured from RTMP"
          />
          <StatCard
            icon={Video} label="VOD Uploads" accent="emerald"
            value={library.vod_uploads.toLocaleString()}
            sub="Direct uploads"
          />
        </div>
        {library.uploads_timeseries?.length > 0 && (
          <Card className="bg-zinc-900/60 border-zinc-800 mt-3">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-zinc-400 uppercase tracking-wide">Uploads · last {days} days</span>
                <span className="text-xs text-violet-400">
                  {library.uploads_timeseries.reduce((acc, d) => acc + d.count, 0)} videos added
                </span>
              </div>
              <Sparkline data={library.uploads_timeseries} accent="#a78bfa" />
            </CardContent>
          </Card>
        )}
      </div>

      {/* ENGAGEMENT STATS */}
      <div>
        <h3 className="text-cyan-400 text-xs uppercase tracking-wider mb-3 font-semibold">Engagement</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            icon={Eye} label="Logged-in Views" accent="cyan"
            value={engagement.logged_in_views_lifetime.toLocaleString()}
            sub={`+${engagement.logged_in_views_in_window} in ${days}d`}
          />
          <StatCard
            icon={Radio} label="Peak Live Viewers" accent="rose"
            value={engagement.peak_concurrent_viewers.toLocaleString()}
            sub="Across all broadcasts"
          />
          <StatCard
            icon={TrendingUp} label="Live Streams Run" accent="emerald"
            value={engagement.total_live_streams_ever.toLocaleString()}
            sub={`${engagement.active_live_streams} active right now`}
          />
          <StatCard
            icon={Scissors} label="Clips Created" accent="amber"
            value={engagement.clips_total.toLocaleString()}
            sub={`+${engagement.clips_created_in_window} in ${days}d`}
          />
        </div>

        {engagement.top_games?.length > 0 && (
          <Card className="bg-zinc-900/60 border-zinc-800 mt-3">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-zinc-400 uppercase tracking-wide font-semibold">Top games by logged-in views</span>
                <span className="text-xs text-zinc-500">All time</span>
              </div>
              <div className="space-y-2">
                {engagement.top_games.slice(0, 10).map((g, idx) => (
                  <div
                    key={g.game_id}
                    className="flex items-center gap-3 py-2 border-b border-zinc-800 last:border-0"
                    data-testid={`top-game-${idx}`}
                  >
                    <span className="text-zinc-500 font-mono text-xs w-6">#{idx + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm truncate">
                        {g.home} <span className="text-zinc-500">vs</span> {g.away}
                      </div>
                      <div className="text-xs text-zinc-500 truncate">
                        {g.event ? `${g.event} · ` : ''}{g.sport} · {g.status}
                      </div>
                    </div>
                    <Badge className="bg-cyan-500/10 text-cyan-400 border-cyan-500/30 shrink-0">
                      {g.logged_in_views} {g.logged_in_views === 1 ? 'view' : 'views'}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
